import React, { useState, useRef, useCallback } from 'react';
import { X, Music, Video as VideoIcon, Youtube, Upload } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { extractYouTubeId } from '../utils/youtube';

interface MediaEmbedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (html: string) => void;
}

type MediaTab = 'audio' | 'video' | 'youtube';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const MAX_AUDIO = 25 * 1024 * 1024; // 25MB
const MAX_VIDEO = 50 * 1024 * 1024; // 50MB
const WRAPPER_STYLE = 'margin:1.5rem 0;';

// Estensioni accettate per tab. Servono come fallback quando il MIME non inizia
// con audio/|video/ (es. .mpeg→video/mpeg in tab audio, .ogg→application/ogg, tipo vuoto).
// .mpeg/.mpg sono ammessi in entrambe le tab (possono essere audio o video).
const AUDIO_EXTS = ['.mp3', '.wav', '.ogg', '.oga', '.m4a', '.aac', '.flac', '.opus', '.weba', '.mpeg', '.mpga'];
const VIDEO_EXTS = ['.mp4', '.webm', '.mov', '.m4v', '.mpeg', '.mpg', '.avi', '.mkv', '.ogv', '.3gp'];
const AUDIO_ACCEPT = 'audio/*,' + AUDIO_EXTS.join(',');
const VIDEO_ACCEPT = 'video/*,' + VIDEO_EXTS.join(',');
const fileExt = (name: string) => {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
};

const MediaEmbedModal: React.FC<MediaEmbedModalProps> = ({ isOpen, onClose, onInsert }) => {
  const toast = useToast();
  const [tab, setTab] = useState<MediaTab>('audio');
  const [autoplay, setAutoplay] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const audioInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const resetAndClose = useCallback(() => {
    setYoutubeUrl('');
    setAutoplay(false);
    setIsUploading(false);
    if (audioInputRef.current) audioInputRef.current.value = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
    onClose();
  }, [onClose]);

  const buildAudioHtml = (url: string) =>
    `<div class="article-media article-audio" contenteditable="false" style="${WRAPPER_STYLE}">` +
    `<audio controls preload="metadata"${autoplay ? ' autoplay muted' : ''} style="width:100%" src="${url}"></audio>` +
    `</div>`;

  const buildVideoHtml = (url: string) =>
    `<div class="article-media article-video" contenteditable="false" style="${WRAPPER_STYLE}">` +
    `<video controls preload="metadata"${autoplay ? ' autoplay muted playsinline' : ''} style="width:100%;max-height:70vh" src="${url}"></video>` +
    `</div>`;

  const buildYoutubeHtml = (id: string) =>
    `<div class="article-media article-video-embed" contenteditable="false" style="${WRAPPER_STYLE}position:relative;padding-bottom:56.25%;height:0;overflow:hidden;">` +
    `<iframe src="https://www.youtube-nocookie.com/embed/${id}${autoplay ? '?autoplay=1&mute=1' : ''}" ` +
    `style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" ` +
    `allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" ` +
    `allowfullscreen title="Video YouTube"></iframe>` +
    `</div>`;

  const uploadAndInsert = useCallback(async (file: File, kind: 'audio' | 'video') => {
    // Validazione tipo: per prefisso MIME oppure, in fallback, per estensione nota
    // (i browser non assegnano sempre un MIME audio/|video/ a file validi).
    const expectedPrefix = kind === 'audio' ? 'audio/' : 'video/';
    const exts = kind === 'audio' ? AUDIO_EXTS : VIDEO_EXTS;
    const typeOk = file.type.startsWith(expectedPrefix);
    if (!typeOk && !exts.includes(fileExt(file.name))) {
      toast.error('Formato non valido', kind === 'audio' ? 'Seleziona un file audio.' : 'Seleziona un file video.');
      return;
    }
    // Validazione dimensione
    const maxSize = kind === 'audio' ? MAX_AUDIO : MAX_VIDEO;
    if (file.size > maxSize) {
      toast.error('File troppo grande', `Massimo ${kind === 'audio' ? '25MB' : '50MB'}.`);
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('media', file);

      const response = await fetch(`${API_BASE}/upload/media`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Errore durante il caricamento');
      }

      const data = await response.json();
      const url = data.file?.url || data.url;
      if (!url) throw new Error('URL del file non disponibile');

      onInsert(kind === 'audio' ? buildAudioHtml(url) : buildVideoHtml(url));
      toast.success(kind === 'audio' ? 'Audio inserito' : 'Video inserito');
      resetAndClose();
    } catch (error: any) {
      console.error('Errore upload media:', error);
      toast.error('Errore durante il caricamento', error?.message);
    } finally {
      setIsUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, onInsert, resetAndClose, toast]);

  const handleInsertYoutube = useCallback(() => {
    const id = extractYouTubeId(youtubeUrl.trim());
    if (!id) {
      toast.error('Link YouTube non valido', 'Incolla un URL come https://www.youtube.com/watch?v=...');
      return;
    }
    onInsert(buildYoutubeHtml(id));
    toast.success('Video YouTube inserito');
    resetAndClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youtubeUrl, autoplay, onInsert, resetAndClose, toast]);

  if (!isOpen) return null;

  const tabs: { id: MediaTab; label: string; icon: React.ReactNode }[] = [
    { id: 'audio', label: 'Audio', icon: <Music className="w-4 h-4" /> },
    { id: 'video', label: 'Video', icon: <VideoIcon className="w-4 h-4" /> },
    { id: 'youtube', label: 'YouTube', icon: <Youtube className="w-4 h-4" /> }
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onMouseDown={resetAndClose}>
      <div
        className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Inserisci contenuto multimediale</h3>
          <button
            type="button"
            onClick={resetAndClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-4">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                tab === t.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {tab === 'audio' && (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Carica un file audio (max 25MB). Formati comuni: MP3, WAV, OGG, M4A.
              </p>
              <button
                type="button"
                onClick={() => audioInputRef.current?.click()}
                disabled={isUploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary-500 hover:text-primary-600 transition-colors disabled:opacity-50"
              >
                <Upload className="w-5 h-5" />
                {isUploading ? 'Caricamento…' : 'Seleziona file audio'}
              </button>
              <input
                ref={audioInputRef}
                type="file"
                accept={AUDIO_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadAndInsert(f, 'audio');
                }}
              />
            </div>
          )}

          {tab === 'video' && (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Carica un file video (max 50MB). Per video pesanti conviene usare YouTube.
              </p>
              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                disabled={isUploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary-500 hover:text-primary-600 transition-colors disabled:opacity-50"
              >
                <Upload className="w-5 h-5" />
                {isUploading ? 'Caricamento…' : 'Seleziona file video'}
              </button>
              <input
                ref={videoInputRef}
                type="file"
                accept={VIDEO_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadAndInsert(f, 'video');
                }}
              />
            </div>
          )}

          {tab === 'youtube' && (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Incolla il link di un video YouTube.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
                <button
                  type="button"
                  onClick={handleInsertYoutube}
                  className="shrink-0 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                >
                  Inserisci
                </button>
              </div>
            </div>
          )}

          {/* Opzione autoplay (comune a tutti i tab) */}
          <label className="flex items-start gap-2 cursor-pointer pt-2 border-t border-gray-100 dark:border-gray-700">
            <input
              type="checkbox"
              checked={autoplay}
              onChange={(e) => setAutoplay(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Avvio automatico al caricamento della pagina
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                I browser consentono l'autoplay solo da silenziato: il contenuto partirà senza audio.
              </span>
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default MediaEmbedModal;
